import { useState, useEffect, useRef } from 'react';
import { env } from '../utils/env';
import * as Sentry from "@sentry/react";

interface Notification {
  id: number;
  type: string;
  message: string;
  // ... other notification fields
}

interface NotificationStatusResponse {
  unread_count: number;
  has_new_notifications: boolean;
  last_checked: string;
}

interface NotificationState {
  unreadCount: number;
  notifications: Notification[];
  status: 'idle' | 'polling' | 'error';
  error: string | null;
  lastActivity: number;
}

// Error types for better error handling
enum NotificationErrorType {
  TIMEOUT = 'timeout',
  PROCESSING_ERROR = 'processing_error',
  NETWORK_ERROR = 'network_error',
  AUTH_ERROR = 'auth_error'
}

interface NotificationError {
  type: NotificationErrorType;
  message: string;
  originalError?: Error | unknown; // Added to carry the original error
}

const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 30000;    // 30 seconds
const MAX_RETRIES = 5;
const POLL_INTERVAL = 60000; // 60 seconds - also checks token validity
const STALE_CONNECTION_THRESHOLD = 180000; // 3 minutes
const REQUEST_TIMEOUT = 15000; // 15 seconds timeout for network requests

export const useUnreadNotifications = (accessToken: string) => {
  const [state, setState] = useState<NotificationState>({
    unreadCount: 0,
    notifications: [],
    status: 'idle',
    error: null,
    lastActivity: Date.now(),
  });

  // Use refs for values that shouldn't trigger re-renders
  const retryCountRef = useRef<number>(0);
  const retryTimeoutRef = useRef<number | undefined>(undefined);
  const pollingIntervalRef = useRef<number | undefined>(undefined);
  const checkConnectionRef = useRef<number | undefined>(undefined);
  const isMountedRef = useRef<boolean>(true);
  const lastActivityRef = useRef<number>(Date.now());
  const onlineListenerAddedRef = useRef<boolean>(false);

  const handleError = (errorType: NotificationErrorType, errorSource: Error | unknown) => {
    const errorMessage = errorSource instanceof Error ? errorSource.message : String(errorSource);
    console.error('Notification Error:', errorType, errorMessage, errorSource);

    // Report error to Sentry with context
    Sentry.withScope(scope => {
      scope.setTag('error_type', errorType);
      scope.setTag('connection_status', state.status); // Use state.status for current status
      scope.setContext('notification_state', {
        retryCount: retryCountRef.current,
        lastActivity: new Date(lastActivityRef.current).toISOString(), // Use ref for most recent activity
      });
      if (errorSource instanceof Error) {
        Sentry.captureException(errorSource);
      } else {
        Sentry.captureMessage(`Notification Error (${errorType}): ${errorMessage}`, 'error');
      }
    });

    const now = Date.now();
    lastActivityRef.current = now;
    setState(prev => ({
      ...prev,
      status: 'error',
      error: errorMessage, // This is the message shown to the user or used in UI state
      lastActivity: now
    }));

    // Handle specific error types
    switch (errorType) {
      case NotificationErrorType.AUTH_ERROR:
        // Don't retry on auth errors
        cleanup();
        break;

      case NotificationErrorType.NETWORK_ERROR:
        // Wait for network to be back, but ensure we only add the listener once
        cleanup(); // Stop any existing intervals

        // Only add the online listener if it hasn't been added yet
        if (!onlineListenerAddedRef.current) {
          window.addEventListener('online', handleOnline);
          onlineListenerAddedRef.current = true;
        }
        break;

      default:
        // For other errors, use exponential backoff
        if (retryCountRef.current < MAX_RETRIES) {
          // Clear any existing retry timeout before setting a new one
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = undefined;
          }

          const delay = Math.min(
            INITIAL_RETRY_DELAY * Math.pow(2, retryCountRef.current),
            MAX_RETRY_DELAY
          );
          retryTimeoutRef.current = window.setTimeout(startPolling, delay);
        }
    }
  };

  // Handler for network reconnection - defined outside to maintain consistent reference
  const handleOnline = () => {
    console.log('[useUnreadNotifications] Network connection restored, restarting polling');

    // Skip if we're already polling
    if (state.status === 'polling') {
      console.log('[useUnreadNotifications] Already polling, skipping online handler');
      return;
    }

    // Remove the listener since we're handling the event now
    window.removeEventListener('online', handleOnline);
    onlineListenerAddedRef.current = false;

    // Start polling again
    startPolling();
  };

  const cleanup = () => {
    // Clean up polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = undefined;
    }

    // Clean up retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = undefined;
    }

    // Clean up connection check interval
    if (checkConnectionRef.current) {
      clearInterval(checkConnectionRef.current);
      checkConnectionRef.current = undefined;
    }

    // Clean up online event listener
    if (onlineListenerAddedRef.current) {
      window.removeEventListener('online', handleOnline);
      onlineListenerAddedRef.current = false;
    }
  };

  // Helper to check if the supplied JWT access token is expired (optionally with a safety buffer)
  const isTokenExpired = (token: string, bufferSeconds: number = 120): boolean => {
    try {
      const base64Url = token.split('.')?.[1];
      if (!base64Url) return true;

      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(base64));
      if (!decoded.exp) return true;

      // Convert both timestamp and current time to seconds for consistent comparison
      const expiryTimeSeconds = decoded.exp;
      const currentTimeSeconds = Math.floor(Date.now() / 1000);

      return currentTimeSeconds >= (expiryTimeSeconds - bufferSeconds);
    } catch (err) {
      // If we cannot decode the token reliably, assume it is valid and let the server validate
      console.warn('[useUnreadNotifications] Failed to decode token for expiry check', err);
      return false;
    }
  };

  const validateAndCheckToken = () => {
    // If no token is available, handle accordingly
    if (!accessToken) {
      cleanup();
      setState(prev => ({
        ...prev,
        status: 'error',
        error: 'No authentication token available.'
      }));
      return false;
    }

    // Check if token is imminently expiring (2 minute buffer)
    if (isTokenExpired(accessToken, 120)) {
      // Stop all polling and immediately clear state to avoid stuck loading state
      cleanup();

      const authErrorMessage = 'Authentication token has expired. Please refresh your session.';
      const authError = new Error(authErrorMessage);

      // Force status to error, never leave it in polling
      setState(prev => ({
        ...prev,
        status: 'error', // Never leave in polling state
        error: authErrorMessage
      }));

      handleError(NotificationErrorType.AUTH_ERROR, authError);
      return false;
    }

    return true;
  };

  const fetchNotificationStatus = async () => {
    // Check token validity before making the request - this happens at each polling interval
    if (!validateAndCheckToken()) {
      return;
    }

    try {
      const startTime = Date.now();
      lastActivityRef.current = startTime;
      setState(prev => ({
        ...prev,
        status: 'polling',
        error: null,
        lastActivity: startTime
      }));

      // Debug log the state change to polling
      console.log('[useUnreadNotifications] Status changed to polling');

      // Create an AbortController for timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, REQUEST_TIMEOUT);

      try {
        const response = await fetch(`${env.API_URL}/api/notifications/status`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
          credentials: 'include',
          signal: controller.signal
        });

        // Clear the timeout once response is received
        clearTimeout(timeoutId);

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Authentication failed');
          }
          throw new Error(`Error fetching notification status: ${response.status}`);
        }

        const data: NotificationStatusResponse = await response.json();

        // Debug log the response from API
        console.log(`[useUnreadNotifications] Received response: unread_count=${data.unread_count}`);

        // Only update state if component is still mounted
        if (isMountedRef.current) {
          const now = Date.now();
          lastActivityRef.current = now;
          setState(prev => ({
            ...prev,
            unreadCount: data.unread_count,
            status: 'idle',  // Make sure this changes from polling to idle
            error: null,
            lastActivity: now,
          }));

          // Debug log the state change to idle
          console.log('[useUnreadNotifications] Status changed to idle');
        } else {
          console.log('[useUnreadNotifications] Component unmounted, state update skipped');
        }

        // Reset retry counter on successful poll
        retryCountRef.current = 0;
      } catch (fetchError) {
        // Clear the timeout
        clearTimeout(timeoutId);

        // Re-throw to be handled by the outer catch block
        throw fetchError;
      }
    } catch (err: any) {
      // Log the raw error first for debugging
      console.error('[useUnreadNotifications] Raw error in fetchNotificationStatus:', err);

      let errorType: NotificationErrorType;
      let userFriendlyMessage = 'Failed to fetch notification status. Please try again.';

      if (err.name === 'AbortError') {
        errorType = NotificationErrorType.TIMEOUT;
        userFriendlyMessage = 'Notification check timed out. Retrying...';
      } else if (err.message && err.message.includes('Authentication failed')) {
        errorType = NotificationErrorType.AUTH_ERROR;
        userFriendlyMessage = 'Authentication failed. Please log in again.';
      } else if (typeof navigator !== 'undefined' && !navigator.onLine) {
        errorType = NotificationErrorType.NETWORK_ERROR;
        userFriendlyMessage = 'Network connection lost. Waiting for connection...';
      } else {
        errorType = NotificationErrorType.PROCESSING_ERROR;
        // Use the error message from the caught error if available, otherwise the generic one
        userFriendlyMessage = err.message || userFriendlyMessage;
      }

      // Ensure err is an Error instance before passing to handleError for Sentry
      const errorToReport = err instanceof Error ? err : new Error(String(err));
      handleError(errorType, errorToReport);
    }
  };

  const startPolling = () => {
    // Clean up any existing timeouts/intervals but do NOT start a new interval.
    cleanup();

    // Validate token before attempting a single refresh.
    if (!validateAndCheckToken()) return;

    // Do a single fetch to refresh the unread count.
    fetchNotificationStatus();
    // NOTE: We intentionally skip setting a recurring setInterval to disable
    // continuous polling for now.  Consumers can call `reconnect()` (which
    // maps to this function) whenever they want a manual refresh.
  };

  // Initial connection and cleanup
  useEffect(() => {
    console.log('[useUnreadNotifications] Initializing notification polling');
    isMountedRef.current = true;

    // Initial token validation
    if (!validateAndCheckToken()) {
      return; // Don't start polling if token is invalid
    }

    // Start polling if token is present and valid
    startPolling();

    // Add window focus event listener to fetch updates and check token when window is focused
    const handleWindowFocus = () => {
      console.log('[useUnreadNotifications] Window focused, checking for updates');

      // Skip if we're already polling or have an auth error
      if (state.status === 'polling') {
        console.log('[useUnreadNotifications] Already polling, skipping focus handler');
        return;
      }

      if (validateAndCheckToken()) {
        // If we already have an interval running, just fetch immediately
        // Otherwise restart polling
        if (pollingIntervalRef.current) {
          fetchNotificationStatus();
        } else {
          startPolling();
        }
      }
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      console.log('[useUnreadNotifications] Cleaning up notification polling');
      isMountedRef.current = false;
      cleanup();
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [accessToken]); // Re-initialize when accessToken changes

  return {
    unreadCount: state.unreadCount,
    notifications: state.notifications,
    status: state.status,
    error: state.error,
    reconnect: startPolling,
    refreshUnreadCount: fetchNotificationStatus
  };
}; 